import { useState, useRef } from 'react';
import axios from 'axios';
import { Camera, Check, Loader2, FileText, X } from 'lucide-react';

import { API_URL } from '../utils/api';
const MAX_PAGES = 6;

export default function DocumentScanner({ sessionId, onError }) {
  const [scanPages, setScanPages] = useState([]);
  const [creatingPdf, setCreatingPdf] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);
  const scanInputRef = useRef(null);

  const handleScanCapture = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const reader = new FileReader();
    reader.onload = (ev) => {
      setScanPages(prev => [...prev, {
        id: Date.now(),
        file,
        preview: ev.target.result
      }]);
    };
    reader.readAsDataURL(file);
    if (scanInputRef.current) scanInputRef.current.value = '';
  };

  const removeScanPage = (id) => {
    setScanPages(prev => prev.filter(p => p.id !== id));
  };

  const createPdf = async () => {
    if (scanPages.length === 0) return;
    setCreatingPdf(true);
    onError('');

    try {
      const formData = new FormData();
      for (const page of scanPages) {
        formData.append('pages', page.file, page.file.name || 'page.jpg');
      }

      await axios.post(
        `${API_URL}/api/photo/pdf/${sessionId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPdfDone(true);
      setScanPages([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      onError(typeof detail === 'string' ? detail : "Erreur lors de la création du PDF");
    } finally {
      setCreatingPdf(false);
    }
  };

  if (pdfDone) {
    return (
      <div className="text-center py-6 space-y-3" data-testid="pdf-done">
        <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <p className="text-green-700 font-medium">PDF envoyé sur le PC !</p>
        <button
          onClick={() => { setPdfDone(false); setScanPages([]); }}
          className="text-sm text-[#6366f1] font-medium underline"
          data-testid="btn-new-scan"
        >
          Scanner un autre document
        </button>
      </div>
    );
  }

  return (
    <>
      {scanPages.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[#2c3e50]">
            {scanPages.length}/{MAX_PAGES} page{scanPages.length > 1 ? 's' : ''}
          </p>
          <div className="flex flex-wrap gap-2" data-testid="scan-pages">
            {scanPages.map((page, idx) => (
              <div key={page.id} className="relative group">
                <div className="w-16 h-20 rounded-lg border-2 border-slate-200 overflow-hidden relative">
                  <img src={page.preview} alt={`Page ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5">
                    p.{idx + 1}
                  </div>
                </div>
                <button
                  onClick={() => removeScanPage(page.id)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                  data-testid={`remove-page-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <input
        ref={scanInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleScanCapture}
        data-testid="scan-input"
      />

      {scanPages.length < MAX_PAGES && (
        <button
          onClick={() => scanInputRef.current?.click()}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:from-[#4f46e5] hover:to-[#7c3aed] text-white font-medium text-base shadow-lg shadow-[#6366f1]/30 transition-all flex items-center justify-center gap-2"
          data-testid="btn-add-page"
        >
          <Camera className="w-5 h-5" />
          {scanPages.length === 0 ? 'Scanner la 1ère page' : `Ajouter page ${scanPages.length + 1}`}
        </button>
      )}

      {scanPages.length >= MAX_PAGES && (
        <div className="text-center py-2 px-3 bg-amber-50 rounded-xl text-amber-700 text-xs font-medium">
          Maximum {MAX_PAGES} pages atteint
        </div>
      )}

      {scanPages.length > 0 && (
        <button
          onClick={createPdf}
          disabled={creatingPdf}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium text-base shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="btn-create-pdf"
        >
          {creatingPdf ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Création du PDF...</>
          ) : (
            <><FileText className="w-5 h-5" /> Créer le PDF ({scanPages.length} page{scanPages.length > 1 ? 's' : ''})</>
          )}
        </button>
      )}
    </>
  );
}
